// Pure completion/schedule math for ProjectCompletionCard.tsx — kept in a component-free file
// (no React/@pulse/ui imports, only pure types from @pulse/shared) so it stays trivially
// unit-testable, matching resourceLevelling.ts: importing it through the component drags in
// MarkdownField -> react-markdown, an ESM-only package Jest can't parse.

import type { IIssue } from '@pulse/shared';

// Same "Done" terminal-status convention already hardcoded in ProjectMetricsPanel.tsx — issues
// don't yet have a reusable "is this status terminal" helper (IWorkflowStep.isTerminal exists
// but nothing reads it), so this matches the existing pattern rather than introducing a new one.
//
// Weighted by originalEstimate (hours) rather than a plain issue count — closing nine 1-hour
// issues while a single 40-hour issue remains open shouldn't read as "90% done". An issue with
// no estimate set falls back to a weight of 1 so the percentage stays defined even when
// estimates are only partially filled in, rather than silently dropping those issues from the
// calculation entirely.
export function completionPercent(issues: readonly Pick<IIssue, 'status' | 'originalEstimate'>[]): number {
  if (issues.length === 0) return 0;
  let totalWeight = 0;
  let doneWeight = 0;
  for (const issue of issues) {
    const weight = issue.originalEstimate ?? 1;
    totalWeight += weight;
    if (issue.status === 'Done') doneWeight += weight;
  }
  return totalWeight === 0 ? 0 : Math.round((doneWeight / totalWeight) * 100);
}

// Straight-line expectation between Start Date and Target Date — 0% before the project starts,
// 100% once the target date has passed, linear in between.
export function expectedCompletionPercent(startDate: string, targetDate: string, today: string): number {
  if (today <= startDate) return 0;
  if (today >= targetDate) return 100;
  // Reaching here means startDate < today < targetDate, so target > start is guaranteed —
  // no zero/negative-division guard needed.
  const start = new Date(startDate).getTime();
  const target = new Date(targetDate).getTime();
  const now = new Date(today).getTime();
  return Math.round(((now - start) / (target - start)) * 100);
}

export type ScheduleStatus = 'completed' | 'onTrack' | 'atRisk' | 'overdue';

export const SCHEDULE_STATUS_CONFIG: Record<ScheduleStatus, { label: string; color: 'success' | 'warning' | 'error' }> = {
  completed: { label: 'Completed', color: 'success' },
  onTrack: { label: 'On Track', color: 'success' },
  atRisk: { label: 'At Risk', color: 'warning' },
  overdue: { label: 'Overdue', color: 'error' },
};

// Requires a Target Date to compare against — Start Date alone can't express an expected pace.
export function scheduleStatus(percent: number, expected: number, targetDate: string, today: string): ScheduleStatus {
  if (percent >= 100) return 'completed';
  if (today > targetDate) return 'overdue';
  return percent >= expected ? 'onTrack' : 'atRisk';
}
